import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Card, CircularLoader, Column, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useBillingSchedule } from '~/hooks/reactQuery/queries/useBillingSchedule';
import { useLocaleKeys } from '~/i18n';
import { utils } from '~/utils';
import styles from './BillingSchedule.module.scss';
import { billingScheduleTextsPath, translateBillingSchedule } from './BillingSchedule.utils';
const emptyBillingSchedule = {
    groups: []
};
export const GetBillingScheduleTID = ()=>getTestIds();
export const BillingSchedule = (param)=>{
    let { services, serviceBundles, effectiveDate, selectedOptionalServices, selectedBundleId, entityContext } = param;
    const { BillingScheduleTestIds } = useMemo(()=>GetBillingScheduleTID(), []);
    const { t: billingScheduleTexts } = useLocaleKeys(billingScheduleTextsPath);
    const { billingSchedule, isLoading } = useBillingSchedule({
        services,
        serviceBundles,
        effectiveDate,
        selectedOptionalServices,
        selectedBundleId,
        entityContext
    });
    const { sections } = useMemo(()=>translateBillingSchedule({
            t: billingScheduleTexts,
            billingSchedule: billingSchedule !== null && billingSchedule !== void 0 ? billingSchedule : emptyBillingSchedule
        }), [
        billingScheduleTexts,
        billingSchedule
    ]);
    const header = /*#__PURE__*/ _jsxs(Column, {
        className: styles.headerOnPrimaryBg,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                weight: "normal",
                testId: BillingScheduleTestIds.Title(),
                children: billingScheduleTexts.title()
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph1",
                weight: "light",
                skin: "secondary",
                testId: BillingScheduleTestIds.Subtitle(),
                children: billingScheduleTexts.subtitle()
            })
        ]
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(Column, {
            width: "100%",
            gap: 8,
            testId: BillingScheduleTestIds.Root(),
            children: [
                header,
                /*#__PURE__*/ _jsx(CircularLoader, {
                    testId: BillingScheduleTestIds.Loader()
                })
            ]
        });
    }
    if (sections.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Column, {
        width: "100%",
        gap: 8,
        testId: BillingScheduleTestIds.Root(),
        children: [
            header,
            sections.length > 0 && /*#__PURE__*/ _jsx(Card, {
                className: styles.billingScheduleCard,
                testId: BillingScheduleTestIds.Card(),
                children: /*#__PURE__*/ _jsx(Column, {
                    gap: 24,
                    children: sections.map((section, sectionIndex)=>{
                        return /*#__PURE__*/ _jsxs(Column, {
                            gap: 12,
                            testId: BillingScheduleTestIds.Section(sectionIndex),
                            children: [
                                /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "paragraph1",
                                    weight: "normal",
                                    testId: BillingScheduleTestIds.SectionTitle(sectionIndex),
                                    children: [
                                        section.title,
                                        !section.isPaused && " - ".concat(utils.formatDollarAmount(section.totalAmount))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: styles.servicesList,
                                    children: section.services.map((service, serviceIndex)=>{
                                        return /*#__PURE__*/ _jsxs("li", {
                                            className: styles.serviceItem,
                                            "data-testid": BillingScheduleTestIds.ServiceItem(sectionIndex, serviceIndex),
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: styles.bullet
                                                }),
                                                /*#__PURE__*/ _jsxs(Column, {
                                                    gap: 2,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            variant: "paragraph1",
                                                            testId: BillingScheduleTestIds.ServiceName(sectionIndex, serviceIndex),
                                                            children: service.name
                                                        }),
                                                        /*#__PURE__*/ _jsx(Column, {
                                                            gap: 2,
                                                            children: /*#__PURE__*/ _jsxs(Row, {
                                                                gap: 4,
                                                                alignItems: "center",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Typography, {
                                                                        variant: "paragraph2",
                                                                        skin: "secondary",
                                                                        testId: BillingScheduleTestIds.ServicePrice(sectionIndex, serviceIndex),
                                                                        children: utils.formatDollarAmount(service.price)
                                                                    }),
                                                                    service.notes.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx(Typography, {
                                                                                variant: "paragraph2",
                                                                                skin: "secondary",
                                                                                children: "•"
                                                                            }),
                                                                            /*#__PURE__*/ _jsx(Typography, {
                                                                                variant: "paragraph2",
                                                                                skin: "secondary",
                                                                                testId: BillingScheduleTestIds.ServiceNotes(sectionIndex, serviceIndex),
                                                                                children: service.notes.join(', ')
                                                                            })
                                                                        ]
                                                                    })
                                                                ]
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, "".concat(service.serviceId, "-").concat(serviceIndex));
                                    })
                                })
                            ]
                        }, section.groupKey);
                    })
                })
            })
        ]
    });
};
